/**
 * Feature flags for toggling experimental features
 */

import { logger } from '@/lib/logger';

export const FeatureFlags = {
  // Enable offline expense repository
  enableOfflineExpenses: () => {
    localStorage.setItem('useOfflineExpenses', 'true');
    logger.info('✅ Offline expenses enabled. Refresh the page to apply.');
    return true;
  },

  // Disable offline expense repository
  disableOfflineExpenses: () => {
    localStorage.removeItem('useOfflineExpenses');
    logger.info('✅ Offline expenses disabled. Using online-only mode. Refresh the page to apply.');
    return false;
  },

  // Check if offline expenses are enabled
  isOfflineExpensesEnabled: () => {
    return localStorage.getItem('useOfflineExpenses') === 'true';
  },

  // Toggle offline expenses
  toggleOfflineExpenses: () => {
    const isEnabled = FeatureFlags.isOfflineExpensesEnabled();
    if (isEnabled) {
      return FeatureFlags.disableOfflineExpenses();
    } else {
      return FeatureFlags.enableOfflineExpenses();
    }
  },

  // Clear all offline data (useful for testing)
  clearOfflineData: async () => {
    // Clear IndexedDB
    const databases = await indexedDB.databases();
    for (const db of databases) {
      if (db.name && db.name.includes('Expenses')) {
        await indexedDB.deleteDatabase(db.name);
        logger.info(`Deleted database: ${db.name}`, { dbName: db.name });
      }
    }

    // Clear localStorage offline expenses
    localStorage.removeItem('offline-expenses');

    logger.info('✅ All offline data cleared.');
    return true;
  },

  // Get offline stats
  getOfflineStats: async () => {
    const { createExpenseRepository } = await import('@/repositories/expense-repository.impl');
    const repo = createExpenseRepository({
      apiUrl: '/api',
      dbName: 'ExpensesDB'
    });

    const stats = await repo.getStats();
    logger.debug('Offline stats:', { stats });
    return stats;
  }
};

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  (window as any).FeatureFlags = FeatureFlags;

  // Show instructions on load
  if (process.env.NODE_ENV === 'development') {
    logger.info('🚀 Feature Flags Available');
    logger.info('Use these commands in the console:');
    logger.info('• FeatureFlags.enableOfflineExpenses() - Enable offline mode');
    logger.info('• FeatureFlags.disableOfflineExpenses() - Disable offline mode');
    logger.info('• FeatureFlags.toggleOfflineExpenses() - Toggle offline mode');
    logger.info('• FeatureFlags.clearOfflineData() - Clear all offline data');
    logger.info('• FeatureFlags.getOfflineStats() - View offline statistics');
    logger.info(`Current status: Offline mode is ${FeatureFlags.isOfflineExpensesEnabled() ? '✅ ENABLED' : '❌ DISABLED'}`);
  }
}